import type { TrackModel } from "../../models/models";
import { AUDIO_TYPE, COLLECTION_IDS } from "../../models/dictionary";

const carrierDescriptions = {
	"80Hz": {
		character: "Deep and resonant.",
		detail:
			"The lowest carrier in the collection, sitting below conventional speech range and closer to physical sensation than tone. On quality headphones or speakers with good low-end response, it can feel more like a presence than a sound. Not all playback systems will render this carrier faithfully. Best experienced with headphones or a speaker setup with strong bass response.",
		bestFor:
			"Deep meditation, body-focused relaxation, or users who want the most immersive, physically felt listening experience in the collection.",
	},
	"100Hz": {
		character: "Warm and low.",
		detail:
			"Sits at the edge of tone and physical sensation. Felt as much as heard on quality headphones or speakers. The most grounding carrier in the collection. Recent research suggests 100Hz tones may also support vestibular balance, making this a good choice for listening in motion.",
		bestFor:
			"Deep relaxation, wind-down sessions, or any blend where you want the sound to feel anchoring rather than present.",
	},
	"200Hz": {
		character: "Full and grounded.",
		detail:
			"A warm mid-range tone that sits comfortably in the background without drawing attention to itself. Balanced enough to layer well with other tracks without competing for presence.",
		bestFor:
			"Long focus sessions, meditation, or as a foundational layer in a multi-track blend.",
	},
	"300Hz": {
		character: "Neutral and balanced.",
		detail:
			"The most sonically neutral carrier in the collection. Neither warm nor bright. Sits in the center of the tonal range and tends to be the least fatiguing over long listening sessions.",
		bestFor:
			"Extended work sessions, background listening, or users who find lower carriers too heavy and higher carriers too sharp.",
	},
	"400Hz": {
		character: "Clear and present.",
		detail:
			"A tonally forward carrier that brings the binaural effect closer to the surface. More energizing than the lower carriers without tipping into brightness.",
		bestFor:
			"Active focus, analytical work, or blends where you want the brainwave effect to feel more immediate.",
	},
	"500Hz": {
		character: "Bright and forward.",
		detail:
			"The most tonally prominent carrier in the collection. Crisp and present, some users find it energizing, others fatiguing over long sessions. Best in shorter bursts or at lower volumes in a blend.",
		bestFor:
			"High-focus sprints, creative sessions, or as a top-layer accent in a multi-track blend.",
	},
};

const isochronicCarrierDescriptions = {
	"80Hz": {
		character: "Deep and resonant.",
		detail:
			"The lowest carrier in the collection, sitting below conventional speech range and closer to physical sensation than tone. The isochronic pulse at this frequency produces a slow, thumping quality that some users find deeply grounding. Not all playback systems will render this carrier faithfully — best experienced with headphones or a speaker setup with strong bass response.",
		bestFor:
			"Deep meditation, body-focused relaxation, or users who want the most physically felt listening experience in the collection.",
	},
	"100Hz": {
		character: "Warm and low.",
		detail:
			"Sits at the edge of tone and physical sensation. The isochronic pulse gives this carrier a rhythmic warmth that feels grounding without heaviness. Recent research suggests 100Hz tones may also support vestibular balance, making this a good choice for listening in motion. Renders well on most headphones and speakers.",
		bestFor:
			"Deep relaxation, wind-down sessions, or any blend where you want the sound to feel anchoring rather than present.",
	},
	"200Hz": {
		character: "Full and grounded.",
		detail:
			"A warm mid-range carrier that balances presence and comfort. The isochronic pulse at 200Hz is perceptible but not intrusive — one of the easier carriers to listen to for extended periods. Layers well without competing for space in a blend.",
		bestFor:
			"Long focus sessions, meditation, or as a foundational layer in a multi-track blend.",
	},
	"300Hz": {
		character: "Neutral and balanced.",
		detail:
			"The most sonically neutral carrier in the collection. The isochronic pulse here sits comfortably in the midrange — neither heavy nor sharp — making it the least fatiguing option for long listening sessions. A reliable default if you're new to isochronic tones.",
		bestFor:
			"Extended work sessions, background listening, or users who find lower carriers too heavy and higher carriers too sharp.",
	},
	"400Hz": {
		character: "Clear and present.",
		detail:
			"A tonally forward carrier where the isochronic pulse becomes more distinct and energizing. The rhythmic quality at this frequency is noticeable enough to feel stimulating without being distracting at moderate volumes. More immediate than the lower carriers.",
		bestFor:
			"Active focus, analytical work, or blends where you want the brainwave effect to feel more engaged.",
	},
	"500Hz": {
		character: "Bright and forward.",
		detail:
			"The most tonally prominent carrier in the collection. At 500Hz the isochronic pulse is crisp and present — the rhythmic quality is at its most noticeable here. Energizing in short sessions, but some users find it fatiguing at higher volumes over time. Best used at moderate volume or as an accent layer in a blend.",
		bestFor:
			"High-focus sprints, creative sessions, or as a top-layer accent in a multi-track blend.",
	},
};

export const brainWaveTracks: TrackModel[] = [
	{
		id: "32cafc8c-955a-440d-b1b2-96c66a12190d",
		audioUrlStub: "https://audios.coffitivity.com/alpha-80hz",
		slug: "alpha-80hz",
		name: "Alpha Waves - 80Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${carrierDescriptions["80Hz"].character} ${carrierDescriptions["80Hz"].detail} ${carrierDescriptions["80Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/alpha",
		albumId: "0512a796-ba42-483d-b61c-4cc59fe45332",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2025-06-25 20:40:50.977605+00",
		updatedAt: "2025-06-25 20:40:50.977605+00",
		releaseDate: "2025-03-03 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:creativity",
			"effects:focus",
			"effects:meditation",
			"effects:mindfulness",
			"effects:relaxation",
		],
	},
	{
		id: "b1ce9df2-c0ec-42fd-bbc1-22dc29041065",
		audioUrlStub: "https://audios.coffitivity.com/alpha-100hz",
		slug: "alpha-100hz",
		name: "Alpha Waves - 100Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${carrierDescriptions["100Hz"].character} ${carrierDescriptions["100Hz"].detail} ${carrierDescriptions["100Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/alpha",
		albumId: "0512a796-ba42-483d-b61c-4cc59fe45332",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-03-11 20:40:50.977605+00",
		updatedAt: "2026-03-11 20:40:50.977605+00",
		releaseDate: "2026-03-11 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:creativity",
			"effects:focus",
			"effects:meditation",
			"effects:mindfulness",
			"effects:relaxation",
		],
	},
	{
		id: "72f3fca0-9cc2-4dda-b861-50c39933c17a",
		audioUrlStub: "https://audios.coffitivity.com/alpha-200hz",
		slug: "alpha-200hz",
		name: "Alpha Waves - 200Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${carrierDescriptions["200Hz"].character} ${carrierDescriptions["200Hz"].detail} ${carrierDescriptions["200Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/alpha",
		albumId: "0512a796-ba42-483d-b61c-4cc59fe45332",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-03-11 20:40:50.977605+00",
		updatedAt: "2026-03-11 20:40:50.977605+00",
		releaseDate: "2026-03-11 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:creativity",
			"effects:focus",
			"effects:meditation",
			"effects:mindfulness",
			"effects:relaxation",
		],
	},
	{
		id: "9b483d14-152c-4c95-9646-197acff56fd6",
		audioUrlStub: "https://audios.coffitivity.com/alpha-300hz",
		slug: "alpha-300hz",
		name: "Alpha Waves - 300Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${carrierDescriptions["300Hz"].character} ${carrierDescriptions["300Hz"].detail} ${carrierDescriptions["300Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/alpha",
		albumId: "0512a796-ba42-483d-b61c-4cc59fe45332",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2025-06-25 20:40:50.977605+00",
		updatedAt: "2025-06-25 20:40:50.977605+00",
		releaseDate: "2025-03-03 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:creativity",
			"effects:focus",
			"effects:meditation",
			"effects:mindfulness",
			"effects:relaxation",
		],
	},
	{
		id: "f222b8ba-652f-4fce-911e-4db4854dd28c",
		audioUrlStub: "https://audios.coffitivity.com/alpha-400hz",
		slug: "alpha-400hz",
		name: "Alpha Waves - 400Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${carrierDescriptions["400Hz"].character} ${carrierDescriptions["400Hz"].detail} ${carrierDescriptions["400Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/alpha",
		albumId: "0512a796-ba42-483d-b61c-4cc59fe45332",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-03-11 20:40:50.977605+00",
		updatedAt: "2026-03-11 20:40:50.977605+00",
		releaseDate: "2026-03-11 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:creativity",
			"effects:focus",
			"effects:meditation",
			"effects:mindfulness",
			"effects:relaxation",
		],
	},
	{
		id: "e4f1b29e-a4b2-4059-80c9-bb0cc215cb7d",
		audioUrlStub: "https://audios.coffitivity.com/alpha-500hz",
		slug: "alpha-500hz",
		name: "Alpha Waves - 500Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${carrierDescriptions["500Hz"].character} ${carrierDescriptions["500Hz"].detail} ${carrierDescriptions["500Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/alpha",
		albumId: "0512a796-ba42-483d-b61c-4cc59fe45332",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-03-11 20:40:50.977605+00",
		updatedAt: "2026-03-11 20:40:50.977605+00",
		releaseDate: "2026-03-11 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:creativity",
			"effects:focus",
			"effects:meditation",
			"effects:mindfulness",
			"effects:relaxation",
		],
	},
	{
		id: "d1028ba7-59ad-4142-ae6a-c8e3c30dac60",
		audioUrlStub: "https://audios.coffitivity.com/beta-80hz",
		slug: "beta-80hz",
		name: "Beta Waves - 80Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${carrierDescriptions["80Hz"].character} ${carrierDescriptions["80Hz"].detail} ${carrierDescriptions["80Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/beta",
		albumId: "6d0e472d-a37c-4ab0-8c28-2d3ca711cb47",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2025-06-25 20:40:50.977605+00",
		updatedAt: "2025-06-25 20:40:50.977605+00",
		releaseDate: "2025-03-03 11:30:06+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:energy",
			"effects:focus",
			"effects:productivity",
			"effects:stimulation",
		],
	},
	{
		id: "fec51d2a-5272-4085-9e39-61fc1985a6dc",
		audioUrlStub: "https://audios.coffitivity.com/beta-100hz",
		slug: "beta-100hz",
		name: "Beta Waves - 100Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${carrierDescriptions["100Hz"].character} ${carrierDescriptions["100Hz"].detail} ${carrierDescriptions["100Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/beta",
		albumId: "6d0e472d-a37c-4ab0-8c28-2d3ca711cb47",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-03-11 20:40:50.977605+00",
		updatedAt: "2026-03-11 20:40:50.977605+00",
		releaseDate: "2026-03-11 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:energy",
			"effects:focus",
			"effects:productivity",
			"effects:stimulation",
		],
	},
	{
		id: "f5772f23-8b77-461c-9623-572155f56156",
		audioUrlStub: "https://audios.coffitivity.com/beta-200hz",
		slug: "beta-200hz",
		name: "Beta Waves - 200Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${carrierDescriptions["200Hz"].character} ${carrierDescriptions["200Hz"].detail} ${carrierDescriptions["200Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/beta",
		albumId: "6d0e472d-a37c-4ab0-8c28-2d3ca711cb47",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-03-11 20:40:50.977605+00",
		updatedAt: "2026-03-11 20:40:50.977605+00",
		releaseDate: "2026-03-11 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:energy",
			"effects:focus",
			"effects:productivity",
			"effects:stimulation",
		],
	},
	{
		id: "387af647-432e-4920-9eab-7e0ef37e7176",
		audioUrlStub: "https://audios.coffitivity.com/beta-300hz",
		slug: "beta-300hz",
		name: "Beta Waves - 300Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${carrierDescriptions["300Hz"].character} ${carrierDescriptions["300Hz"].detail} ${carrierDescriptions["300Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/beta",
		albumId: "6d0e472d-a37c-4ab0-8c28-2d3ca711cb47",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-03-11 20:40:50.977605+00",
		updatedAt: "2026-03-11 20:40:50.977605+00",
		releaseDate: "2026-03-11 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:energy",
			"effects:focus",
			"effects:productivity",
			"effects:stimulation",
		],
	},
	{
		id: "0c451896-2f6e-4910-8ffd-57c822fa238b",
		audioUrlStub: "https://audios.coffitivity.com/beta-400hz",
		slug: "beta-400hz",
		name: "Beta Waves - 400Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${carrierDescriptions["400Hz"].character} ${carrierDescriptions["400Hz"].detail} ${carrierDescriptions["400Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/beta",
		albumId: "6d0e472d-a37c-4ab0-8c28-2d3ca711cb47",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2025-06-25 20:40:50.977605+00",
		updatedAt: "2025-06-25 20:40:50.977605+00",
		releaseDate: "2025-03-03 11:30:06+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:energy",
			"effects:focus",
			"effects:productivity",
			"effects:stimulation",
		],
	},
	{
		id: "2323d4c0-f346-4991-9c9b-1d39bd2528e4",
		audioUrlStub: "https://audios.coffitivity.com/beta-500hz",
		slug: "beta-500hz",
		name: "Beta Waves - 500Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${carrierDescriptions["500Hz"].character} ${carrierDescriptions["500Hz"].detail} ${carrierDescriptions["500Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/beta",
		albumId: "6d0e472d-a37c-4ab0-8c28-2d3ca711cb47",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-03-11 20:40:50.977605+00",
		updatedAt: "2026-03-11 20:40:50.977605+00",
		releaseDate: "2026-03-11 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:energy",
			"effects:focus",
			"effects:productivity",
			"effects:stimulation",
		],
	},
	{
		id: "fb6b7fd3-f96b-4441-98ca-108f8dd6f3ad",
		audioUrlStub: "https://audios.coffitivity.com/delta-80hz",
		slug: "delta-80hz",
		name: "Delta Waves - 80Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${carrierDescriptions["80Hz"].character} ${carrierDescriptions["80Hz"].detail} ${carrierDescriptions["80Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/delta",
		albumId: "48ff31a9-912c-4c00-9636-b608c8d7c7b7",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2025-06-25 20:40:50.977605+00",
		updatedAt: "2025-06-25 20:40:50.977605+00",
		releaseDate: "2025-03-03 11:30:09+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:meditation",
			"effects:mindfulness",
			"effects:relaxation",
			"effects:sleep",
		],
	},
	{
		id: "c6e99ae8-74e2-415e-83b3-6312db5750da",
		audioUrlStub: "https://audios.coffitivity.com/delta-100hz",
		slug: "delta-100hz",
		name: "Delta Waves - 100Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${carrierDescriptions["100Hz"].character} ${carrierDescriptions["100Hz"].detail} ${carrierDescriptions["100Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/delta",
		albumId: "48ff31a9-912c-4c00-9636-b608c8d7c7b7",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-03-11 20:40:50.977605+00",
		updatedAt: "2026-03-11 20:40:50.977605+00",
		releaseDate: "2026-03-11 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:meditation",
			"effects:mindfulness",
			"effects:relaxation",
			"effects:sleep",
		],
	},
	{
		id: "f80b37a6-fd47-4b2f-9efe-777edeedd614",
		audioUrlStub: "https://audios.coffitivity.com/delta-200hz",
		slug: "delta-200hz",
		name: "Delta Waves - 200Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${carrierDescriptions["200Hz"].character} ${carrierDescriptions["200Hz"].detail} ${carrierDescriptions["200Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/delta",
		albumId: "48ff31a9-912c-4c00-9636-b608c8d7c7b7",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2025-06-25 20:40:50.977605+00",
		updatedAt: "2025-06-25 20:40:50.977605+00",
		releaseDate: "2025-03-03 11:30:09+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:meditation",
			"effects:mindfulness",
			"effects:relaxation",
			"effects:sleep",
		],
	},
	{
		id: "31e4e197-b343-420e-8e5d-ac6fc5751670",
		audioUrlStub: "https://audios.coffitivity.com/delta-300hz",
		slug: "delta-300hz",
		name: "Delta Waves - 300Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${carrierDescriptions["300Hz"].character} ${carrierDescriptions["300Hz"].detail} ${carrierDescriptions["300Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/delta",
		albumId: "48ff31a9-912c-4c00-9636-b608c8d7c7b7",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-03-11 20:40:50.977605+00",
		updatedAt: "2026-03-11 20:40:50.977605+00",
		releaseDate: "2026-03-11 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:meditation",
			"effects:mindfulness",
			"effects:relaxation",
			"effects:sleep",
		],
	},
	{
		id: "7f8f5582-6667-49e6-8566-debe72d4fa72",
		audioUrlStub: "https://audios.coffitivity.com/delta-400hz",
		slug: "delta-400hz",
		name: "Delta Waves - 400Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${carrierDescriptions["400Hz"].character} ${carrierDescriptions["400Hz"].detail} ${carrierDescriptions["400Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/delta",
		albumId: "48ff31a9-912c-4c00-9636-b608c8d7c7b7",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-03-11 20:40:50.977605+00",
		updatedAt: "2026-03-11 20:40:50.977605+00",
		releaseDate: "2026-03-11 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:meditation",
			"effects:mindfulness",
			"effects:relaxation",
			"effects:sleep",
		],
	},
	{
		id: "48ec7729-2183-4601-82f5-d66962586fd4",
		audioUrlStub: "https://audios.coffitivity.com/delta-500hz",
		slug: "delta-500hz",
		name: "Delta Waves - 500Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${carrierDescriptions["500Hz"].character} ${carrierDescriptions["500Hz"].detail} ${carrierDescriptions["500Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/delta",
		albumId: "48ff31a9-912c-4c00-9636-b608c8d7c7b7",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-03-11 20:40:50.977605+00",
		updatedAt: "2026-03-11 20:40:50.977605+00",
		releaseDate: "2026-03-11 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:meditation",
			"effects:mindfulness",
			"effects:relaxation",
			"effects:sleep",
		],
	},
	{
		id: "db24e94b-a4af-43ae-8c31-898c4ab5a8bc",
		audioUrlStub: "https://audios.coffitivity.com/gamma-80hz",
		slug: "gamma-80hz",
		name: "Gamma Waves - 80Hz Tone",
		type: AUDIO_TYPE.TRACK,
		description: `${carrierDescriptions["80Hz"].character} ${carrierDescriptions["80Hz"].detail} ${carrierDescriptions["80Hz"].bestFor}`,
		isPremium: true,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/gamma",
		albumId: "5d8afeca-8ca4-4cd5-a69c-ec53fdb5889c",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2025-06-25 20:40:50.977605+00",
		updatedAt: "2025-06-25 20:40:50.977605+00",
		releaseDate: "2025-03-03 11:30:05+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:energy",
			"effects:focus",
			"effects:productivity",
			"effects:stimulation",
		],
	},
	{
		id: "96857519-6ff8-491f-ae3f-68fcb3bc385c",
		audioUrlStub: "https://audios.coffitivity.com/gamma-100hz",
		slug: "gamma-100hz",
		name: "Gamma Waves - 100Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${carrierDescriptions["100Hz"].character} ${carrierDescriptions["100Hz"].detail} ${carrierDescriptions["100Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/gamma",
		albumId: "5d8afeca-8ca4-4cd5-a69c-ec53fdb5889c",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-03-11 20:40:50.977605+00",
		updatedAt: "2026-03-11 20:40:50.977605+00",
		releaseDate: "2026-03-11 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:energy",
			"effects:focus",
			"effects:productivity",
			"effects:stimulation",
		],
	},
	{
		id: "205db1fe-9425-461b-b8c8-9ba95b744dbb",
		audioUrlStub: "https://audios.coffitivity.com/gamma-200hz",
		slug: "gamma-200hz",
		name: "Gamma Waves - 200Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${carrierDescriptions["200Hz"].character} ${carrierDescriptions["200Hz"].detail} ${carrierDescriptions["200Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/gamma",
		albumId: "5d8afeca-8ca4-4cd5-a69c-ec53fdb5889c",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-03-11 20:40:50.977605+00",
		updatedAt: "2026-03-11 20:40:50.977605+00",
		releaseDate: "2026-03-11 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:energy",
			"effects:focus",
			"effects:productivity",
			"effects:stimulation",
		],
	},
	{
		id: "b8e204d4-9d1d-4bbb-9d40-ab7f0adb57b9",
		audioUrlStub: "https://audios.coffitivity.com/gamma-300hz",
		slug: "gamma-300hz",
		name: "Gamma Waves - 300Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${carrierDescriptions["300Hz"].character} ${carrierDescriptions["300Hz"].detail} ${carrierDescriptions["300Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/gamma",
		albumId: "5d8afeca-8ca4-4cd5-a69c-ec53fdb5889c",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-03-11 20:40:50.977605+00",
		updatedAt: "2026-03-11 20:40:50.977605+00",
		releaseDate: "2026-03-11 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:energy",
			"effects:focus",
			"effects:productivity",
			"effects:stimulation",
		],
	},
	{
		id: "88df0a23-369c-4999-a0e1-19f15ee5a4d7",
		audioUrlStub: "https://audios.coffitivity.com/gamma-400hz",
		slug: "gamma-400hz",
		name: "Gamma Waves - 400Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${carrierDescriptions["400Hz"].character} ${carrierDescriptions["400Hz"].detail} ${carrierDescriptions["400Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/gamma",
		albumId: "5d8afeca-8ca4-4cd5-a69c-ec53fdb5889c",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-03-11 20:40:50.977605+00",
		updatedAt: "2026-03-11 20:40:50.977605+00",
		releaseDate: "2026-03-11 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:energy",
			"effects:focus",
			"effects:productivity",
			"effects:stimulation",
		],
	},
	{
		id: "db62a850-ea24-45a0-9295-5558b73dba5c",
		audioUrlStub: "https://audios.coffitivity.com/gamma-500hz",
		slug: "gamma-500hz",
		name: "Gamma Waves - 500Hz Tone",
		type: AUDIO_TYPE.TRACK,
		description: `${carrierDescriptions["500Hz"].character} ${carrierDescriptions["500Hz"].detail} ${carrierDescriptions["500Hz"].bestFor}`,
		isPremium: true,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/gamma",
		albumId: "5d8afeca-8ca4-4cd5-a69c-ec53fdb5889c",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2025-06-25 20:40:50.977605+00",
		updatedAt: "2025-06-25 20:40:50.977605+00",
		releaseDate: "2025-03-03 11:30:05+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:energy",
			"effects:focus",
			"effects:productivity",
			"effects:stimulation",
		],
	},
	{
		id: "3a765ab7-0133-42b5-8913-6c931d530322",
		audioUrlStub: "https://audios.coffitivity.com/theta-80hz",
		slug: "theta-80hz",
		name: "Theta Waves - 80Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${carrierDescriptions["80Hz"].character} ${carrierDescriptions["80Hz"].detail} ${carrierDescriptions["80Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/theta",
		albumId: "96e3eb57-72f1-49d7-8872-8e2e6082806d",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2025-06-25 20:40:50.977605+00",
		updatedAt: "2025-06-25 20:40:50.977605+00",
		releaseDate: "2025-03-03 11:30:08+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:creativity",
			"effects:meditation",
			"effects:mindfulness",
			"effects:relaxation",
			"effects:sleep",
		],
	},
	{
		id: "b5402485-6174-442a-b7b3-08555cb2cfea",
		audioUrlStub: "https://audios.coffitivity.com/theta-100hz",
		slug: "theta-100hz",
		name: "Theta Waves - 100Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${carrierDescriptions["100Hz"].character} ${carrierDescriptions["100Hz"].detail} ${carrierDescriptions["100Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/theta",
		albumId: "96e3eb57-72f1-49d7-8872-8e2e6082806d",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-03-11 20:40:50.977605+00",
		updatedAt: "2026-03-11 20:40:50.977605+00",
		releaseDate: "2026-03-11 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:creativity",
			"effects:meditation",
			"effects:mindfulness",
			"effects:relaxation",
			"effects:sleep",
		],
	},
	{
		id: "0779221c-0369-4eb2-ad24-544c74985530",
		audioUrlStub: "https://audios.coffitivity.com/theta-200hz",
		slug: "theta-200hz",
		name: "Theta Waves - 200Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${carrierDescriptions["200Hz"].character} ${carrierDescriptions["200Hz"].detail} ${carrierDescriptions["200Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/theta",
		albumId: "96e3eb57-72f1-49d7-8872-8e2e6082806d",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2025-06-25 20:40:50.977605+00",
		updatedAt: "2025-06-25 20:40:50.977605+00",
		releaseDate: "2025-03-03 11:30:08+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:creativity",
			"effects:meditation",
			"effects:mindfulness",
			"effects:relaxation",
			"effects:sleep",
		],
	},
	{
		id: "4693df70-4652-4139-ba63-0e7517308177",
		audioUrlStub: "https://audios.coffitivity.com/theta-300hz",
		slug: "theta-300hz",
		name: "Theta Waves - 300Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${carrierDescriptions["300Hz"].character} ${carrierDescriptions["300Hz"].detail} ${carrierDescriptions["300Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/theta",
		albumId: "96e3eb57-72f1-49d7-8872-8e2e6082806d",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-03-11 20:40:50.977605+00",
		updatedAt: "2026-03-11 20:40:50.977605+00",
		releaseDate: "2026-03-11 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:creativity",
			"effects:meditation",
			"effects:mindfulness",
			"effects:relaxation",
			"effects:sleep",
		],
	},
	{
		id: "af22ab44-e552-4944-861f-86309199592e",
		audioUrlStub: "https://audios.coffitivity.com/theta-400hz",
		slug: "theta-400hz",
		name: "Theta Waves - 400Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${carrierDescriptions["400Hz"].character} ${carrierDescriptions["400Hz"].detail} ${carrierDescriptions["400Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/theta",
		albumId: "96e3eb57-72f1-49d7-8872-8e2e6082806d",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-03-11 20:40:50.977605+00",
		updatedAt: "2026-03-11 20:40:50.977605+00",
		releaseDate: "2026-03-11 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:creativity",
			"effects:meditation",
			"effects:mindfulness",
			"effects:relaxation",
			"effects:sleep",
		],
	},
	{
		id: "70334aaf-5b88-476c-8d4c-0b5baa6491cd",
		audioUrlStub: "https://audios.coffitivity.com/theta-500hz",
		slug: "theta-500hz",
		name: "Theta Waves - 500Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${carrierDescriptions["500Hz"].character} ${carrierDescriptions["500Hz"].detail} ${carrierDescriptions["500Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/theta",
		albumId: "96e3eb57-72f1-49d7-8872-8e2e6082806d",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-03-11 20:40:50.977605+00",
		updatedAt: "2026-03-11 20:40:50.977605+00",
		releaseDate: "2026-03-11 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:creativity",
			"effects:meditation",
			"effects:mindfulness",
			"effects:relaxation",
			"effects:sleep",
		],
	},
	{
		id: "24ba15c6-3fb9-4d15-a24a-6f93c9532b82",
		audioUrlStub: "https://audios.coffitivity.com/iso-alpha-80hz",
		slug: "iso-alpha-80hz",
		name: "Isochronic Alpha Waves - 80Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${isochronicCarrierDescriptions["80Hz"].character} ${isochronicCarrierDescriptions["80Hz"].detail} ${isochronicCarrierDescriptions["80Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/alpha",
		albumId: "ac13c87b-0625-4a37-8715-15004f169c37",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-05-18 20:40:50.977605+00",
		updatedAt: "2026-05-18 20:40:50.977605+00",
		releaseDate: "2026-05-18 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:creativity",
			"effects:focus",
			"effects:meditation",
			"effects:mindfulness",
			"effects:relaxation",
		],
	},
	{
		id: "fc6ee0ae-bb8b-4d72-951c-9dbe88ab029f",
		audioUrlStub: "https://audios.coffitivity.com/iso-alpha-100hz",
		slug: "iso-alpha-100hz",
		name: "Isochronic Alpha Waves - 100Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${isochronicCarrierDescriptions["100Hz"].character} ${isochronicCarrierDescriptions["100Hz"].detail} ${isochronicCarrierDescriptions["100Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/alpha",
		albumId: "ac13c87b-0625-4a37-8715-15004f169c37",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-05-18 20:40:50.977605+00",
		updatedAt: "2026-05-18 20:40:50.977605+00",
		releaseDate: "2026-05-18 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:creativity",
			"effects:focus",
			"effects:meditation",
			"effects:mindfulness",
			"effects:relaxation",
		],
	},
	{
		id: "36259db4-1492-4b40-a598-dfc2b54daba1",
		audioUrlStub: "https://audios.coffitivity.com/iso-alpha-200hz",
		slug: "iso-alpha-200hz",
		name: "Isochronic Alpha Waves - 200Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${isochronicCarrierDescriptions["200Hz"].character} ${isochronicCarrierDescriptions["200Hz"].detail} ${isochronicCarrierDescriptions["200Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/alpha",
		albumId: "ac13c87b-0625-4a37-8715-15004f169c37",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-05-18 20:40:50.977605+00",
		updatedAt: "2026-05-18 20:40:50.977605+00",
		releaseDate: "2026-05-18 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:creativity",
			"effects:focus",
			"effects:meditation",
			"effects:mindfulness",
			"effects:relaxation",
		],
	},
	{
		id: "66f35e33-e4a2-44d4-bb37-140105bcbc06",
		audioUrlStub: "https://audios.coffitivity.com/iso-alpha-300hz",
		slug: "iso-alpha-300hz",
		name: "Isochronic Alpha Waves - 300Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${isochronicCarrierDescriptions["300Hz"].character} ${isochronicCarrierDescriptions["300Hz"].detail} ${isochronicCarrierDescriptions["300Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/alpha",
		albumId: "ac13c87b-0625-4a37-8715-15004f169c37",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-05-18 20:40:50.977605+00",
		updatedAt: "2026-05-18 20:40:50.977605+00",
		releaseDate: "2026-05-18 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:creativity",
			"effects:focus",
			"effects:meditation",
			"effects:mindfulness",
			"effects:relaxation",
		],
	},
	{
		id: "a8dec14a-c9ae-4f8f-964c-7004408f0ae9",
		audioUrlStub: "https://audios.coffitivity.com/iso-alpha-400hz",
		slug: "iso-alpha-400hz",
		name: "Isochronic Alpha Waves - 400Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${isochronicCarrierDescriptions["400Hz"].character} ${isochronicCarrierDescriptions["400Hz"].detail} ${isochronicCarrierDescriptions["400Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/alpha",
		albumId: "ac13c87b-0625-4a37-8715-15004f169c37",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-05-18 20:40:50.977605+00",
		updatedAt: "2026-05-18 20:40:50.977605+00",
		releaseDate: "2026-05-18 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:creativity",
			"effects:focus",
			"effects:meditation",
			"effects:mindfulness",
			"effects:relaxation",
		],
	},
	{
		id: "062668bc-a3ac-452b-87fb-1c707175c970",
		audioUrlStub: "https://audios.coffitivity.com/iso-alpha-500hz",
		slug: "iso-alpha-500hz",
		name: "Isochronic Alpha Waves - 500Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${isochronicCarrierDescriptions["500Hz"].character} ${isochronicCarrierDescriptions["500Hz"].detail} ${isochronicCarrierDescriptions["500Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/alpha",
		albumId: "ac13c87b-0625-4a37-8715-15004f169c37",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-05-18 20:40:50.977605+00",
		updatedAt: "2026-05-18 20:40:50.977605+00",
		releaseDate: "2026-05-18 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:creativity",
			"effects:focus",
			"effects:meditation",
			"effects:mindfulness",
			"effects:relaxation",
		],
	},
	{
		id: "75ecdd8f-4fdd-49ab-9407-9ae64a1c9720",
		audioUrlStub: "https://audios.coffitivity.com/iso-beta-80hz",
		slug: "iso-beta-80hz",
		name: "Isochronic Beta Waves - 80Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${isochronicCarrierDescriptions["80Hz"].character} ${isochronicCarrierDescriptions["80Hz"].detail} ${isochronicCarrierDescriptions["80Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/beta",
		albumId: "a4e830ae-28ab-40fa-9e49-93dd625ef0fe",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-05-18 20:40:50.977605+00",
		updatedAt: "2026-05-18 20:40:50.977605+00",
		releaseDate: "2026-05-18 11:30:06+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:energy",
			"effects:focus",
			"effects:productivity",
			"effects:stimulation",
		],
	},
	{
		id: "829a583b-e774-4242-8630-e6e11a354b2f",
		audioUrlStub: "https://audios.coffitivity.com/iso-beta-100hz",
		slug: "iso-beta-100hz",
		name: "Isochronic Beta Waves - 100Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${isochronicCarrierDescriptions["100Hz"].character} ${isochronicCarrierDescriptions["100Hz"].detail} ${isochronicCarrierDescriptions["100Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/beta",
		albumId: "a4e830ae-28ab-40fa-9e49-93dd625ef0fe",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-05-18 20:40:50.977605+00",
		updatedAt: "2026-05-18 20:40:50.977605+00",
		releaseDate: "2026-05-18 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:energy",
			"effects:focus",
			"effects:productivity",
			"effects:stimulation",
		],
	},
	{
		id: "56ebd715-0a3f-47cb-bc2d-bad318ac3a6d",
		audioUrlStub: "https://audios.coffitivity.com/iso-beta-200hz",
		slug: "iso-beta-200hz",
		name: "Isochronic Beta Waves - 200Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${isochronicCarrierDescriptions["200Hz"].character} ${isochronicCarrierDescriptions["200Hz"].detail} ${isochronicCarrierDescriptions["200Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/beta",
		albumId: "a4e830ae-28ab-40fa-9e49-93dd625ef0fe",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-05-18 20:40:50.977605+00",
		updatedAt: "2026-05-18 20:40:50.977605+00",
		releaseDate: "2026-05-18 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:energy",
			"effects:focus",
			"effects:productivity",
			"effects:stimulation",
		],
	},
	{
		id: "019e76a6-6c09-493e-9777-c89f6c4d4577",
		audioUrlStub: "https://audios.coffitivity.com/iso-beta-300hz",
		slug: "iso-beta-300hz",
		name: "Isochronic Beta Waves - 300Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${isochronicCarrierDescriptions["300Hz"].character} ${isochronicCarrierDescriptions["300Hz"].detail} ${isochronicCarrierDescriptions["300Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/beta",
		albumId: "a4e830ae-28ab-40fa-9e49-93dd625ef0fe",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-05-18 20:40:50.977605+00",
		updatedAt: "2026-05-18 20:40:50.977605+00",
		releaseDate: "2026-05-18 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:energy",
			"effects:focus",
			"effects:productivity",
			"effects:stimulation",
		],
	},
	{
		id: "a5a7d8b8-23e8-4945-bf39-953500f68103",
		audioUrlStub: "https://audios.coffitivity.com/iso-beta-400hz",
		slug: "iso-beta-400hz",
		name: "Isochronic Beta Waves - 400Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${isochronicCarrierDescriptions["400Hz"].character} ${isochronicCarrierDescriptions["400Hz"].detail} ${isochronicCarrierDescriptions["400Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/beta",
		albumId: "a4e830ae-28ab-40fa-9e49-93dd625ef0fe",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-05-18 20:40:50.977605+00",
		updatedAt: "2026-05-18 20:40:50.977605+00",
		releaseDate: "2026-05-18 11:30:06+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:energy",
			"effects:focus",
			"effects:productivity",
			"effects:stimulation",
		],
	},
	{
		id: "f004a1cd-a1c0-45ee-bfb8-7d52568dac16",
		audioUrlStub: "https://audios.coffitivity.com/iso-beta-500hz",
		slug: "iso-beta-500hz",
		name: "Isochronic Beta Waves - 500Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${isochronicCarrierDescriptions["500Hz"].character} ${isochronicCarrierDescriptions["500Hz"].detail} ${isochronicCarrierDescriptions["500Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/beta",
		albumId: "a4e830ae-28ab-40fa-9e49-93dd625ef0fe",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-05-18 20:40:50.977605+00",
		updatedAt: "2026-05-18 20:40:50.977605+00",
		releaseDate: "2026-05-18 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:energy",
			"effects:focus",
			"effects:productivity",
			"effects:stimulation",
		],
	},
	{
		id: "a5813139-502e-4161-ba2a-38cbf45e9358",
		audioUrlStub: "https://audios.coffitivity.com/iso-delta-80hz",
		slug: "iso-delta-80hz",
		name: "Isochronic Delta Waves - 80Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${isochronicCarrierDescriptions["80Hz"].character} ${isochronicCarrierDescriptions["80Hz"].detail} ${isochronicCarrierDescriptions["80Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/delta",
		albumId: "95b6d34b-1533-464c-8004-db625435fc1c",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-05-18 20:40:50.977605+00",
		updatedAt: "2026-05-18 20:40:50.977605+00",
		releaseDate: "2026-05-18 11:30:09+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:meditation",
			"effects:mindfulness",
			"effects:relaxation",
			"effects:sleep",
		],
	},
	{
		id: "d8c46793-cb25-419f-91ab-7fbb6ebab472",
		audioUrlStub: "https://audios.coffitivity.com/iso-delta-100hz",
		slug: "iso-delta-100hz",
		name: "Isochronic Delta Waves - 100Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${isochronicCarrierDescriptions["100Hz"].character} ${isochronicCarrierDescriptions["100Hz"].detail} ${isochronicCarrierDescriptions["100Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/delta",
		albumId: "95b6d34b-1533-464c-8004-db625435fc1c",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-05-18 20:40:50.977605+00",
		updatedAt: "2026-05-18 20:40:50.977605+00",
		releaseDate: "2026-05-18 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:meditation",
			"effects:mindfulness",
			"effects:relaxation",
			"effects:sleep",
		],
	},
	{
		id: "65dc1d8d-3090-440a-9858-3bf20c14c871",
		audioUrlStub: "https://audios.coffitivity.com/iso-delta-200hz",
		slug: "iso-delta-200hz",
		name: "Isochronic Delta Waves - 200Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${isochronicCarrierDescriptions["200Hz"].character} ${isochronicCarrierDescriptions["200Hz"].detail} ${isochronicCarrierDescriptions["200Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/delta",
		albumId: "95b6d34b-1533-464c-8004-db625435fc1c",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-05-18 20:40:50.977605+00",
		updatedAt: "2026-05-18 20:40:50.977605+00",
		releaseDate: "2026-05-18 11:30:09+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:meditation",
			"effects:mindfulness",
			"effects:relaxation",
			"effects:sleep",
		],
	},
	{
		id: "f1b70652-80d4-4699-a01b-c6216c52368d",
		audioUrlStub: "https://audios.coffitivity.com/iso-delta-300hz",
		slug: "iso-delta-300hz",
		name: "Isochronic Delta Waves - 300Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${isochronicCarrierDescriptions["300Hz"].character} ${isochronicCarrierDescriptions["300Hz"].detail} ${isochronicCarrierDescriptions["300Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/delta",
		albumId: "95b6d34b-1533-464c-8004-db625435fc1c",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-05-18 20:40:50.977605+00",
		updatedAt: "2026-05-18 20:40:50.977605+00",
		releaseDate: "2026-05-18 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:meditation",
			"effects:mindfulness",
			"effects:relaxation",
			"effects:sleep",
		],
	},
	{
		id: "5f864776-e8eb-4b6b-86e3-7a079e9d2f82",
		audioUrlStub: "https://audios.coffitivity.com/iso-delta-400hz",
		slug: "iso-delta-400hz",
		name: "Isochronic Delta Waves - 400Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${isochronicCarrierDescriptions["400Hz"].character} ${isochronicCarrierDescriptions["400Hz"].detail} ${isochronicCarrierDescriptions["400Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/delta",
		albumId: "95b6d34b-1533-464c-8004-db625435fc1c",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-05-18 20:40:50.977605+00",
		updatedAt: "2026-05-18 20:40:50.977605+00",
		releaseDate: "2026-05-18 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:meditation",
			"effects:mindfulness",
			"effects:relaxation",
			"effects:sleep",
		],
	},
	{
		id: "065265bf-e572-4753-b09e-3e7c5de355b1",
		audioUrlStub: "https://audios.coffitivity.com/iso-delta-500hz",
		slug: "iso-delta-500hz",
		name: "Isochronic Delta Waves - 500Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${isochronicCarrierDescriptions["500Hz"].character} ${isochronicCarrierDescriptions["500Hz"].detail} ${isochronicCarrierDescriptions["500Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/delta",
		albumId: "95b6d34b-1533-464c-8004-db625435fc1c",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-05-18 20:40:50.977605+00",
		updatedAt: "2026-05-18 20:40:50.977605+00",
		releaseDate: "2026-05-18 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:meditation",
			"effects:mindfulness",
			"effects:relaxation",
			"effects:sleep",
		],
	},
	{
		id: "7019decb-169e-4de0-b6a2-8cfc75d5b9ed",
		audioUrlStub: "https://audios.coffitivity.com/iso-gamma-80hz",
		slug: "iso-gamma-80hz",
		name: "Isochronic Gamma Waves - 80Hz Tone",
		type: AUDIO_TYPE.TRACK,
		description: `${isochronicCarrierDescriptions["80Hz"].character} ${isochronicCarrierDescriptions["80Hz"].detail} ${isochronicCarrierDescriptions["80Hz"].bestFor}`,
		isPremium: true,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/gamma",
		albumId: "85c5c208-b0c2-4f6a-bb78-61d83d3406f8",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-05-18 20:40:50.977605+00",
		updatedAt: "2026-05-18 20:40:50.977605+00",
		releaseDate: "2026-05-18 11:30:05+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:energy",
			"effects:focus",
			"effects:productivity",
			"effects:stimulation",
		],
	},
	{
		id: "c8bbe107-d9a6-4027-b95e-2fcaf779463b",
		audioUrlStub: "https://audios.coffitivity.com/iso-gamma-100hz",
		slug: "iso-gamma-100hz",
		name: "Isochronic Gamma Waves - 100Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${isochronicCarrierDescriptions["100Hz"].character} ${isochronicCarrierDescriptions["100Hz"].detail} ${isochronicCarrierDescriptions["100Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/gamma",
		albumId: "85c5c208-b0c2-4f6a-bb78-61d83d3406f8",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-05-18 20:40:50.977605+00",
		updatedAt: "2026-05-18 20:40:50.977605+00",
		releaseDate: "2026-05-18 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:energy",
			"effects:focus",
			"effects:productivity",
			"effects:stimulation",
		],
	},
	{
		id: "0bd6596f-c183-40bc-a5d6-68c573b65601",
		audioUrlStub: "https://audios.coffitivity.com/iso-gamma-200hz",
		slug: "iso-gamma-200hz",
		name: "Isochronic Gamma Waves - 200Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${isochronicCarrierDescriptions["200Hz"].character} ${isochronicCarrierDescriptions["200Hz"].detail} ${isochronicCarrierDescriptions["200Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/gamma",
		albumId: "85c5c208-b0c2-4f6a-bb78-61d83d3406f8",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-05-18 20:40:50.977605+00",
		updatedAt: "2026-05-18 20:40:50.977605+00",
		releaseDate: "2026-05-18 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:energy",
			"effects:focus",
			"effects:productivity",
			"effects:stimulation",
		],
	},
	{
		id: "c21788c5-f20c-4757-b99f-1775e6b1b648",
		audioUrlStub: "https://audios.coffitivity.com/iso-gamma-300hz",
		slug: "iso-gamma-300hz",
		name: "Isochronic Gamma Waves - 300Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${isochronicCarrierDescriptions["300Hz"].character} ${isochronicCarrierDescriptions["300Hz"].detail} ${isochronicCarrierDescriptions["300Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/gamma",
		albumId: "85c5c208-b0c2-4f6a-bb78-61d83d3406f8",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-05-18 20:40:50.977605+00",
		updatedAt: "2026-05-18 20:40:50.977605+00",
		releaseDate: "2026-05-18 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:energy",
			"effects:focus",
			"effects:productivity",
			"effects:stimulation",
		],
	},
	{
		id: "fc685cbb-e001-42e9-97ee-f47d5d7a3d1b",
		audioUrlStub: "https://audios.coffitivity.com/iso-gamma-400hz",
		slug: "iso-gamma-400hz",
		name: "Isochronic Gamma Waves - 400Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${isochronicCarrierDescriptions["400Hz"].character} ${isochronicCarrierDescriptions["400Hz"].detail} ${isochronicCarrierDescriptions["400Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/gamma",
		albumId: "85c5c208-b0c2-4f6a-bb78-61d83d3406f8",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-05-18 20:40:50.977605+00",
		updatedAt: "2026-05-18 20:40:50.977605+00",
		releaseDate: "2026-05-18 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:energy",
			"effects:focus",
			"effects:productivity",
			"effects:stimulation",
		],
	},
	{
		id: "ea386585-4f78-4818-b74e-0c749d701916",
		audioUrlStub: "https://audios.coffitivity.com/iso-gamma-500hz",
		slug: "iso-gamma-500hz",
		name: "Isochronic Gamma Waves - 500Hz Tone",
		type: AUDIO_TYPE.TRACK,
		description: `${isochronicCarrierDescriptions["500Hz"].character} ${isochronicCarrierDescriptions["500Hz"].detail} ${isochronicCarrierDescriptions["500Hz"].bestFor}`,
		isPremium: true,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/gamma",
		albumId: "85c5c208-b0c2-4f6a-bb78-61d83d3406f8",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-05-18 20:40:50.977605+00",
		updatedAt: "2026-05-18 20:40:50.977605+00",
		releaseDate: "2026-05-18 11:30:05+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:energy",
			"effects:focus",
			"effects:productivity",
			"effects:stimulation",
		],
	},
	{
		id: "b7998d79-4271-49b8-bd03-5917ff024091",
		audioUrlStub: "https://audios.coffitivity.com/iso-theta-80hz",
		slug: "iso-theta-80hz",
		name: "Isochronic Theta Waves - 80Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${isochronicCarrierDescriptions["80Hz"].character} ${isochronicCarrierDescriptions["80Hz"].detail} ${isochronicCarrierDescriptions["80Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/theta",
		albumId: "d5d1eb0c-d15b-4b31-b4ae-80924ba74e96",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-05-18 20:40:50.977605+00",
		updatedAt: "2026-05-18 20:40:50.977605+00",
		releaseDate: "2026-05-18 11:30:08+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:creativity",
			"effects:meditation",
			"effects:mindfulness",
			"effects:relaxation",
			"effects:sleep",
		],
	},
	{
		id: "e3ea2ffa-32dd-4189-8196-fbdabcf14e5d",
		audioUrlStub: "https://audios.coffitivity.com/iso-theta-100hz",
		slug: "iso-theta-100hz",
		name: "Isochronic Theta Waves - 100Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${isochronicCarrierDescriptions["100Hz"].character} ${isochronicCarrierDescriptions["100Hz"].detail} ${isochronicCarrierDescriptions["100Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/theta",
		albumId: "d5d1eb0c-d15b-4b31-b4ae-80924ba74e96",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-05-18 20:40:50.977605+00",
		updatedAt: "2026-05-18 20:40:50.977605+00",
		releaseDate: "2026-05-18 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:creativity",
			"effects:meditation",
			"effects:mindfulness",
			"effects:relaxation",
			"effects:sleep",
		],
	},
	{
		id: "ede3d993-2504-458a-a772-4451121b1390",
		audioUrlStub: "https://audios.coffitivity.com/iso-theta-200hz",
		slug: "iso-theta-200hz",
		name: "Isochronic Theta Waves - 200Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${isochronicCarrierDescriptions["200Hz"].character} ${isochronicCarrierDescriptions["200Hz"].detail} ${isochronicCarrierDescriptions["200Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/theta",
		albumId: "d5d1eb0c-d15b-4b31-b4ae-80924ba74e96",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-05-18 20:40:50.977605+00",
		updatedAt: "2026-05-18 20:40:50.977605+00",
		releaseDate: "2026-05-18 11:30:08+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:creativity",
			"effects:meditation",
			"effects:mindfulness",
			"effects:relaxation",
			"effects:sleep",
		],
	},
	{
		id: "38e27537-e79b-4c06-ae44-41f8e9842c33",
		audioUrlStub: "https://audios.coffitivity.com/iso-theta-300hz",
		slug: "iso-theta-300hz",
		name: "Isochronic Theta Waves - 300Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${isochronicCarrierDescriptions["300Hz"].character} ${isochronicCarrierDescriptions["300Hz"].detail} ${isochronicCarrierDescriptions["300Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/theta",
		albumId: "d5d1eb0c-d15b-4b31-b4ae-80924ba74e96",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-05-18 20:40:50.977605+00",
		updatedAt: "2026-05-18 20:40:50.977605+00",
		releaseDate: "2026-05-18 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:creativity",
			"effects:meditation",
			"effects:mindfulness",
			"effects:relaxation",
			"effects:sleep",
		],
	},
	{
		id: "f8720fc6-3839-44b7-976f-c61a4d37442f",
		audioUrlStub: "https://audios.coffitivity.com/iso-theta-400hz",
		slug: "iso-theta-400hz",
		name: "Isochronic Theta Waves - 400Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${isochronicCarrierDescriptions["400Hz"].character} ${isochronicCarrierDescriptions["400Hz"].detail} ${isochronicCarrierDescriptions["400Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/theta",
		albumId: "d5d1eb0c-d15b-4b31-b4ae-80924ba74e96",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-05-18 20:40:50.977605+00",
		updatedAt: "2026-05-18 20:40:50.977605+00",
		releaseDate: "2026-05-18 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:creativity",
			"effects:meditation",
			"effects:mindfulness",
			"effects:relaxation",
			"effects:sleep",
		],
	},
	{
		id: "e3878b78-2ae7-4e07-9665-98ed0c02bdd7",
		audioUrlStub: "https://audios.coffitivity.com/iso-theta-500hz",
		slug: "iso-theta-500hz",
		name: "Isochronic Theta Waves - 500Hz Tone",
		type: AUDIO_TYPE.TRACK,
		isPremium: true,
		description: `${isochronicCarrierDescriptions["500Hz"].character} ${isochronicCarrierDescriptions["500Hz"].detail} ${isochronicCarrierDescriptions["500Hz"].bestFor}`,
		isLooping: true,
		imageUrlStub: "https://images.coffitivity.com/theta",
		albumId: "d5d1eb0c-d15b-4b31-b4ae-80924ba74e96",
		location: null,
		blend: null,
		userId: null,
		createdAt: "2026-05-18 20:40:50.977605+00",
		updatedAt: "2026-05-18 20:40:50.977605+00",
		releaseDate: "2026-05-18 11:30:07+00",
		collectionId: COLLECTION_IDS.BRAIN_WAVE,
		effectIds: [
			"effects:creativity",
			"effects:meditation",
			"effects:mindfulness",
			"effects:relaxation",
			"effects:sleep",
		],
	},
];
